import {
  Appointment,
  OrganisationCache,
  PatientContextBuilder,
  ScopeDataBuilder,
} from '@principle-theorem/principle-core';
import {
  type IAppointment,
  type IPatient,
  IPractice,
  type IPrincipleMention,
  type IResolvedTemplateContext,
  type ITemplateContextOption,
  MentionResourceType,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { type IProvider, asDocRef, getDoc } from '@principle-theorem/shared';
import { type DocumentReference } from '@principle-theorem/shared';
import { memoize } from 'lodash';

export class PatientMentionContextProvider
  implements IProvider<TemplateScope[], Promise<ITemplateContextOption[]>>
{
  constructor(
    private _mention: IPrincipleMention,
    private _practiceRef: DocumentReference<IPractice>,
    private _appUrl: string
  ) {}

  canProvide(scopes: TemplateScope[]): boolean {
    const type = this._mention.resource.type;
    const isPatientScope = scopes.includes(TemplateScope.Patient);
    const isPatientResource = type === MentionResourceType.Patient;
    const isAppointmentResource = type === MentionResourceType.Appointment;
    return isPatientScope && (isPatientResource || isAppointmentResource);
  }

  async execute(): Promise<ITemplateContextOption[]> {
    const isAppointmentResource: boolean =
      this._mention.resource.type === MentionResourceType.Appointment;

    if (isAppointmentResource) {
      return this._resolvePatientContextsFromAppointment();
    }

    return [
      this._resolvePatientContext(
        this._mention.resource.ref as DocumentReference<IPatient>,
        this._practiceRef
      ),
    ];
  }

  private async _resolvePatientContextsFromAppointment(): Promise<
    ITemplateContextOption[]
  > {
    const appointment = await getDoc(
      asDocRef<IAppointment>(this._mention.resource.ref)
    );

    return [
      this._resolvePatientContext(
        Appointment.patientRef(appointment),
        appointment.practice.ref
      ),
    ];
  }

  private _resolvePatientContext(
    patientRef: DocumentReference<IPatient>,
    practiceRef: DocumentReference<IPractice>
  ): ITemplateContextOption {
    return {
      label: this._mention.key,
      scope: TemplateScope.Patient,
      resolve: memoize(() =>
        this._buildPatientContext(patientRef, practiceRef)
      ),
    };
  }

  private async _buildPatientContext(
    patientRef: DocumentReference<IPatient>,
    practiceRef: DocumentReference<IPractice>
  ): Promise<IResolvedTemplateContext> {
    const patient = await OrganisationCache.patients.getDoc(patientRef);
    const practice = await OrganisationCache.practices.getDoc(practiceRef);
    const scopeData = await ScopeDataBuilder.buildPatientScopeData(
      patient,
      practice,
      this._appUrl
    );
    const context = new PatientContextBuilder(scopeData).build();

    return { context, scopeData };
  }
}
